import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet, apiPut } from "../../services/api";
import type { CartItem } from "../../types";

export default function RefundModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [lookupId, setLookupId] = useState("");
  const [transaction, setTransaction] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refunding, setRefunding] = useState(false);
  const [success, setSuccess] = useState("");
  const [selectedItems, setSelectedItems] = useState<Record<number, { selected: boolean; qty: number; serialNumber?: string }>>({});

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lookupId.trim()) return;
    setError(""); setTransaction(null); setSuccess(""); setSelectedItems({});
    setLoading(true);
    try {
      const txn = await apiGet<any>(`/organizations/${orgId}/transactions/${lookupId.trim()}`);
      if (txn.status === "refunded") {
        setError("This transaction has already been fully refunded.");
      } else if (txn.status === "voided") {
        setError("This transaction has been voided.");
      } else {
        setTransaction(txn);
        const sel: Record<number, { selected: boolean; qty: number }> = {};
        (txn.items || []).forEach((_: any, i: number) => { sel[i] = { selected: false, qty: txn.items[i].quantity }; });
        setSelectedItems(sel);
      }
    } catch {
      setError("Transaction not found. Check the receipt number.");
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (idx: number) => {
    const item = transaction.items[idx];
    setSelectedItems((prev) => ({ ...prev, [idx]: { selected: !prev[idx]?.selected, qty: prev[idx]?.qty || item?.quantity || 1, serialNumber: prev[idx]?.serialNumber || "" } }));
  };

  const updateReturnQty = (idx: number, qty: number) => {
    const maxQty = transaction.items[idx].quantity;
    setSelectedItems((prev) => ({ ...prev, [idx]: { ...prev[idx], qty: Math.min(Math.max(1, qty), maxQty) } }));
  };

  const updateSerialNumber = (idx: number, serialNumber: string) => {
    setSelectedItems((prev) => ({ ...prev, [idx]: { ...prev[idx], serialNumber } }));
  };

  const getRefundItems = (): CartItem[] => {
    if (!transaction) return [];
    return Object.entries(selectedItems)
      .filter(([_, v]) => v.selected)
      .map(([idx, v]) => {
        const item = transaction.items[parseInt(idx)];
        return { ...item, quantity: v.qty, serialNumber: v.serialNumber || item.serialNumber };
      });
  };

  const refundItems = getRefundItems();
  const refundSubtotal = refundItems.reduce((sum, i) => sum + Math.abs(i.price) * i.quantity, 0);
  const refundTaxRate = transaction?.taxRate || 0;
  const refundTaxableAmount = refundItems.filter((i) => i.taxable !== false).reduce((sum, i) => sum + Math.abs(i.price) * i.quantity, 0);
  const refundTax = Math.round(refundTaxableAmount * refundTaxRate * 100) / 100;
  const refundTotal = Math.round((refundSubtotal + refundTax) * 100) / 100;
  const anySelected = refundItems.length > 0;

  const needsSerial = getRefundItems().some((item) => {
    const origItem = transaction?.items?.find((i: any) => i.productId === item.productId);
    return origItem?.requiresSerialNumber && !item.serialNumber;
  });

  const handleRefund = async () => {
    if (!transaction || !anySelected) return;
    if (needsSerial) { setError("Please scan serial numbers for all serialized items."); return; }

    const returnedItems = getRefundItems();
    const allItemsReturned = returnedItems.length === transaction.items.length &&
      returnedItems.every((item, i) => item.quantity === transaction.items[i]?.quantity);

    if (!confirm(`Process return of $${refundTotal.toFixed(2)} for ${returnedItems.length} item(s)?`)) return;
    setRefunding(true);
    setError("");

    try {
      // Build returns array (append to existing returns if partial was already done)
      const existingReturns = transaction.returns || [];
      const newReturn = {
        refundSubtotal,
        refundTax,
        returnedAt: new Date().toISOString(),
        returnedBy: user?.id || "",
        items: returnedItems,
        refundAmount: refundTotal,
      };

      const updatedTransaction = {
        ...transaction,
        status: allItemsReturned ? "refunded" : "partial-refund",
        returns: [...existingReturns, newReturn],
        refundedTotal: (transaction.refundedTotal || 0) + refundTotal,
      };

      await apiPut(`/organizations/${orgId}/transactions/${transaction.transactionId}`, updatedTransaction);

      setSuccess(`Return of $${refundTotal.toFixed(2)} processed. Transaction updated.`);
      setTransaction(null);
      setLookupId("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Return failed");
    } finally {
      setRefunding(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "550px", maxHeight: "85vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <h3 style={{ margin: 0 }}>↩️ Returns / Refunds</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer" }}>✕</button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: "1rem" }}>{error}</div>}
        {success && <div style={{ background: "#dcfce7", border: "1px solid #86efac", color: "#166534", padding: "0.65rem", borderRadius: "6px", fontSize: "0.85rem", marginBottom: "1rem" }}>{success}</div>}

        <form onSubmit={handleLookup}>
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.25rem" }}>
            <input
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value.toUpperCase())}
              placeholder="Scan or enter receipt #"
              style={{ flex: 1, padding: "0.7rem 0.85rem", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "1.05rem", fontFamily: "monospace", letterSpacing: "1px" }}
              autoFocus
            />
            <button type="submit" className="btn btn-primary" style={{ width: "auto" }} disabled={loading || !lookupId.trim()}>
              {loading ? "..." : "Find"}
            </button>
          </div>
        </form>

        {transaction && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1rem", padding: "0.75rem", background: "#f9fafb", borderRadius: "6px" }}>
              <div>
                <div style={{ fontWeight: 600 }}>Receipt #{transaction.receiptNumber}</div>
                <div style={{ color: "#6b7280", fontSize: "0.8rem" }}>{new Date(transaction.createdAt).toLocaleString()}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 600 }}>${(transaction.total || 0).toFixed(2)}</div>
                <div style={{ color: "#6b7280", fontSize: "0.8rem" }}>{transaction.paymentMethod}</div>
              </div>
            </div>

            {/* Show previous returns if any */}
            {transaction.returns?.length > 0 && (
              <div style={{ background: "#fef2f2", padding: "0.6rem 0.75rem", borderRadius: "6px", marginBottom: "1rem", fontSize: "0.8rem", color: "#991b1b" }}>
                ⚠️ Previous returns: ${(transaction.refundedTotal || 0).toFixed(2)} already refunded
              </div>
            )}

            <p style={{ fontSize: "0.85rem", color: "#6b7280", marginBottom: "0.75rem" }}>Select items to return:</p>

            <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
              {(transaction.items || []).map((item: any, idx: number) => {
                const sel = selectedItems[idx];
                const requiresSerial = item.requiresSerialNumber || item.serialNumber;
                return (
                  <div key={idx} style={{ padding: "0.75rem", borderBottom: "1px solid #f3f4f6", background: sel?.selected ? "#eff6ff" : "white" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <input type="checkbox" checked={sel?.selected || false} onChange={() => toggleItem(idx)} style={{ width: "18px", height: "18px" }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, fontSize: "0.9rem" }}>{item.name}</div>
                        <div style={{ color: "#6b7280", fontSize: "0.8rem" }}>
                          ${Math.abs(item.price).toFixed(2)} × {item.quantity}
                          {item.serialNumber && <span style={{ marginLeft: "0.5rem", color: "#7c3aed" }}>SN: {item.serialNumber}</span>}
                        </div>
                      </div>
                      {sel?.selected && (
                        <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                          <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>Qty:</span>
                          <input type="number" min="1" max={item.quantity} value={sel?.qty || 1} onChange={(e) => updateReturnQty(idx, parseInt(e.target.value))} style={{ width: "50px", padding: "0.25rem", border: "1px solid #d1d5db", borderRadius: "4px", textAlign: "center" }} />
                        </div>
                      )}
                    </div>
                    {sel?.selected && requiresSerial && (
                      <div style={{ marginTop: "0.5rem", marginLeft: "2rem" }}>
                        <input
                          value={sel.serialNumber || ""}
                          onChange={(e) => updateSerialNumber(idx, e.target.value)}
                          placeholder="Scan serial number for return..."
                          style={{ width: "100%", padding: "0.4rem 0.6rem", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "0.85rem", fontFamily: "monospace" }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {anySelected && (
              <div style={{ marginTop: "1rem", padding: "0.75rem", background: "#fef2f2", borderRadius: "6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div><span style={{ fontWeight: 600 }}>Refund: ${refundTotal.toFixed(2)}</span><div style={{ fontSize: "0.75rem", color: "#6b7280" }}>Subtotal: ${refundSubtotal.toFixed(2)} + Tax: ${refundTax.toFixed(2)}</div></div>
                <button onClick={handleRefund} disabled={refunding || needsSerial} style={{ padding: "0.6rem 1.25rem", background: "#dc2626", color: "white", border: "none", borderRadius: "6px", fontWeight: 600, cursor: "pointer" }}>
                  {refunding ? "Processing..." : "Process Return"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
